import { StandardEvents } from '@shopify/events';

/**
 * Keeps the "Buy Now" quick-checkout permalink in sync with the currently
 * selected variant. The link is rendered server-side for the initial variant
 * (progressive enhancement); this element updates it when the customer picks a
 * different variant via the theme's variant picker.
 *
 * The checkout permalink `/cart/{variant_id}:1` adds the variant to the cart
 * and redirects straight to checkout, bypassing the cart page.
 */
class QuickCheckoutButton extends HTMLElement {
  /** @type {HTMLAnchorElement | null} */
  #link = null;
  /** @type {HTMLElement | null} */
  #labelEl = null;
  /** @type {Element | null} */
  #section = null;
  /** @type {string} */
  #cartBase = '/cart';

  connectedCallback() {
    this.#link = this.querySelector('[data-quick-checkout-link]');
    this.#labelEl = this.querySelector('[data-quick-checkout-label]');
    this.#cartBase = this.dataset.cartBase || '/cart';

    this.#link?.addEventListener('click', this.#onClick);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#link?.removeEventListener('click', this.#onClick);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.#link?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
    }
  };

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;
        const { resource } = detail;
        const variantId = resource ? resource.id : null;
        const available = resource ? resource.available : false;
        this.#setVariant(variantId, available);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-checkout] Event promise rejected:', error);
      });
  };

  /**
   * @param {string | number | null} variantId
   * @param {boolean} available
   */
  #setVariant(variantId, available) {
    const link = this.#link;
    if (!link) return;

    if (variantId && available) {
      link.href = `${this.#cartBase}/${variantId}:1`;
      link.removeAttribute('aria-disabled');
      link.removeAttribute('tabindex');
      const label = link.dataset.labelDefault;
      if (this.#labelEl && label) this.#labelEl.textContent = label;
    } else {
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
      link.removeAttribute('href');
      const soldOut = link.dataset.labelSoldOut;
      if (this.#labelEl && soldOut) this.#labelEl.textContent = soldOut;
    }
  }
}

if (!customElements.get('quick-checkout-button')) {
  customElements.define('quick-checkout-button', QuickCheckoutButton);
}
